import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import getsuvLogo from './getsuv-logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

const LoadingIndicator = () => {
  const [statusIndex, setStatusIndex] = useState(0);
  const statuses = [
    "Analyzing your query...",
    "Searching Suvai database...",
    "Optimizing results...",
    "Consulting AI brain...",
    "Finalizing answer..."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % statuses.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [statuses.length]);

  return (
    <div className="loading-container">
      <div className="typing-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div className="loading-status-text">{statuses[statusIndex]}</div>
    </div>
  );
};

const MapComponent = ({ lat, lon, userLocation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapUrl = `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${userLocation?.lat},${userLocation?.lon}&destination=${lat},${lon}&mode=driving`;
  const externalMapUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLocation?.lat},${userLocation?.lon}&destination=${lat},${lon}&travelmode=driving`;

  return (
    <div className="map-wrapper" style={{ marginTop: '5px' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: '#f8f9fa',
          color: '#e21c15',
          border: '1px solid #e21c15',
          padding: '5px 10px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 'bold',
          marginBottom: '5px'
        }}
      >
        {isOpen ? 'Close map' : 'Open to see navigation'}
      </button>
      {isOpen && userLocation && (
        <div className="map-container" style={{ borderRadius: '10px', overflow: 'hidden' }}>
          <iframe
            title="Navigation Map"
            width="100%"
            height="250"
            frameBorder="0"
            style={{ border: 0 }}
            src={mapUrl}
            allowFullScreen
          ></iframe>
          <a
            href={externalMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              marginTop: '5px',
              textAlign: 'center',
              padding: '8px',
              backgroundColor: '#ffffff',
              color: '#e21c15',
              textDecoration: 'none',
              fontWeight: 'bold',
              borderRadius: '8px',
              border: '1px solid #e21c15',
              fontSize: '13px'
            }}
          >
            Start Navigation
          </a>
        </div>
      )}
    </div>
  );
};

const StoreList = ({ initialStores, allStores, userLocation }) => {
  const [visibleCount, setVisibleCount] = useState(10);
  const storesToUse = allStores || initialStores;
  const currentStores = storesToUse.slice(0, visibleCount);
  const hasMore = visibleCount < storesToUse.length;

  return (
    <>
      {currentStores.map((store, idx) => (
        <div key={idx} className="store-card">
          <div className="store-name">{store.store_name}</div>
          <div className="store-details">
            {store.address && <span>📍 {store.address}</span>}
            <span>📞 {store.phone ? <a href={`tel:${store.phone}`} className="phone-link">{store.phone}</a> : "Contact store for details"}</span>
            <span>📏 Distance: <b>{store.distance}</b></span>
            <span className="availability-badge">✅ {store.availability || 'In Stock'}</span>
          </div>
          <div className="store-note">
            <b>Note:</b> Please confirm availability by calling the store.
          </div>
          {store.lat && store.lon && (
            <MapComponent lat={store.lat} lon={store.lon} userLocation={userLocation} />
          )}
        </div>
      ))}
      {hasMore && (
        <button
          className="show-more-btn"
          onClick={() => setVisibleCount(prev => prev + 10)}
        >
          Show More ({storesToUse.length - visibleCount} remaining)
        </button>
      )}
    </>
  );
};

function App() {
  const isWidget = new URLSearchParams(window.location.search).get('widget') === 'true';

  useEffect(() => {
    if (isWidget) {
      document.documentElement.classList.add('widget-html');
      document.body.classList.add('widget-body');
    } else {
      document.documentElement.classList.remove('widget-html');
      document.body.classList.remove('widget-body');
    }
    return () => {
      document.documentElement.classList.remove('widget-html');
      document.body.classList.remove('widget-body');
    };
  }, [isWidget]);

  // Provide the welcome message instantly to fix LCP (Largest Contentful Paint)
  const [messages, setMessages] = useState([
    { id: 1, text: "getSUV.AI welcomes you! How may I assist you with product search today?", sender: 'bot' }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  // gps = device location | pincode = manual override | none = not set yet
  const [locationSource, setLocationSource] = useState('none');
  const [locationLabel, setLocationLabel] = useState('');
  const [pendingSearch, setPendingSearch] = useState(null);
  const [isWaitingForLocation, setIsWaitingForLocation] = useState(false);
  const messagesEndRef = useRef(null);
  // Prevent late GPS callback from overwriting a user-selected pincode
  const locationSourceRef = useRef('none');

  const setLocationContext = (source, label, coords) => {
    locationSourceRef.current = source;
    setLocationSource(source);
    setLocationLabel(label || '');
    if (coords) {
      setUserLocation(coords);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  // Initial Geolocation tracking in the background so it doesn't block the UI
  useEffect(() => {
    if ("geolocation" in navigator) {
      try {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // Do not override an explicit pincode / postal code selection
            if (locationSourceRef.current === 'pincode') {
              return;
            }
            const { latitude, longitude } = position.coords;
            setLocationContext('gps', 'Current location', { lat: latitude, lon: longitude });
            setMessages(prev => [
              ...prev,
              { id: `${Date.now()}-${Math.random()}`, text: "📍 I have received your location. Searching for nearby stores will now be more accurate! Use **Change location** anytime to search near a home postal code instead.", sender: 'bot' }
            ]);
          },
          (error) => {
            console.warn("Location permission denied or error:", error);
          }
        );
      } catch (err) {
        console.warn("Geolocation permission error:", err);
      }
    }
  }, []);

  const executeChatSearch = async (messageText, location, updatedMessages) => {
    try {
      const history = updatedMessages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: typeof msg.text === 'string' ? msg.text : '',
        data: msg.data || null
      }));

      const body = {
        message: messageText,
        history: history,
        latitude: location?.lat,
        longitude: location?.lon
      };

      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // If backend asks for location, enter waiting-for-location state on frontend
      if (data.type === 'location_required') {
        setPendingSearch(data.product_name);
        setIsWaitingForLocation(true);
      }

      const botMessage = {
        id: `${Date.now()}-${Math.random()}`,
        sender: 'bot',
        responseType: data.type || 'chat_response',
        data: data,
        text: data.message || data.response || ''
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [
        ...prev,
        { id: `${Date.now()}-${Math.random()}`, text: "Sorry, I couldn't get the requested information right now. Please try another product or try again in a few moments.", sender: 'bot' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = { id: `${Date.now()}-${Math.random()}`, text: messageText, sender: 'user' };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");

    // CASE 1: Waiting for postal code / address (GPS missing OR Change location override)
    if (isWaitingForLocation) {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/geocode`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ address: messageText })
        });

        if (!response.ok) {
          throw new Error('Failed to resolve location.');
        }

        const data = await response.json();
        const resolvedLoc = { lat: data.latitude, lon: data.longitude };
        const displayLabel = data.resolved_address || messageText.trim();
        setLocationContext('pincode', displayLabel, resolvedLoc);
        setIsWaitingForLocation(false);

        const confirmText = `📍 Search location set to **${displayLabel}**. Store results will use this area.`;
        setMessages(prev => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random()}`,
            text: confirmText,
            sender: 'bot'
          }
        ]);

        // Resume pending product search (location_required flow)
        if (pendingSearch) {
          const currentSearch = pendingSearch;
          setPendingSearch(null);

          setMessages(prev => [
            ...prev,
            {
              id: `${Date.now()}-1-${Math.random()}`,
              text: `🔍 Searching for **${currentSearch}** within a 10km radius...`,
              sender: 'bot'
            }
          ]);

          await executeChatSearch(currentSearch, resolvedLoc, [
            ...updatedMessages,
            { id: `${Date.now()}-2-${Math.random()}`, text: confirmText, sender: 'bot' },
            { id: `${Date.now()}-3-${Math.random()}`, text: `🔍 Searching for **${currentSearch}** within a 10km radius...`, sender: 'bot' }
          ]);
        } else {
          setMessages(prev => [
            ...prev,
            { id: `${Date.now()}-4-${Math.random()}`, text: "What product would you like to search for?", sender: 'bot' }
          ]);
          setLoading(false);
        }
      } catch (err) {
        setMessages(prev => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random()}`,
            text: `⚠️ I couldn't find coordinates for "${messageText}". Please try a valid postal code, suburb, or address:`,
            sender: 'bot'
          }
        ]);
        setLoading(false);
      }
      return;
    }

    // Default flow: Send message to backend (uses active GPS or pincode location)
    setLoading(true);
    await executeChatSearch(messageText, userLocation, updatedMessages);
  };

  const handleChangeLocation = () => {
    if (loading) return;
    setPendingSearch(null);
    setIsWaitingForLocation(true);
    setMessages(prev => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        text: "📍 Enter your home / preferred area postal code (or suburb/address). Product search will use that location instead of your current GPS location.",
        sender: 'bot'
      }
    ]);
  };

  const handleUseCurrentLocation = () => {
    if (loading) return;
    if (!("geolocation" in navigator)) {
      setMessages(prev => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          text: "⚠️ Geolocation is not supported on this device. Please enter a postal code instead.",
          sender: 'bot'
        }
      ]);
      return;
    }

    setLoading(true);
    setIsWaitingForLocation(false);
    setPendingSearch(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocationContext('gps', 'Current location', { lat: latitude, lon: longitude });
        setMessages(prev => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random()}`,
            text: "📍 Search location set back to **your current location**. Store results will use GPS.",
            sender: 'bot'
          }
        ]);
        setLoading(false);
      },
      (error) => {
        console.warn("Failed to restore GPS location:", error);
        setMessages(prev => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random()}`,
            text: "⚠️ I couldn't access your current location. Please allow location permission, or enter a postal code using Change location.",
            sender: 'bot'
          }
        ]);
        setLoading(false);
      }
    );
  };

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
  };

  const handleOptionSelect = (option) => {
    sendMessage(option);
  };

  const locationStatusText = (() => {
    if (isWaitingForLocation) return 'Enter a postal code to search stores';
    if (locationSource === 'pincode' && locationLabel) return `Stores near ${locationLabel}`;
    if (locationSource === 'gps') return 'Stores near you';
    return 'Location not set';
  })();

  const renderMessageContent = (msg) => {
    // User messages are always plain text
    if (msg.sender === 'user') {
      return <div>{msg.text}</div>;
    }

    const responseType = msg.responseType;
    const data = msg.data;

    // --- SELECTION REQUIRED: Show option buttons ---
    if (responseType === 'selection_required' && data?.options) {
      const selectionMessage = (data.message || '').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br/>');
      return (
        <div className="selection-container">
          <div className="selection-message" dangerouslySetInnerHTML={{ __html: selectionMessage }} />
          <div className="options-list">
            {data.options.map((opt, idx) => (
              <button
                key={idx}
                className="option-button"
                onClick={() => handleOptionSelect(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // --- STRUCTURED RESULTS: Show store cards for product search ---
    if (responseType === 'store_results' && data?.stores) {
      const numStores = data.total_found || data.stores.length;
      const storeWord = numStores === 1 ? 'store' : 'stores';
      const searchTarget = data.product_name || data.product || 'the product';

      const rawHeader = data.message || `There ${numStores === 1 ? 'is' : 'are'} ${numStores > 0 ? numStores : 'no'} ${storeWord} near you with `;
      const formattedHeader = rawHeader.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br/>');

      return (
        <div className="store-results-container">
          <div className="store-results-header">
            ✅ <b>Product Availability Found</b><br />
            <span dangerouslySetInnerHTML={{ __html: formattedHeader }} />
            {!data.message && <b>{searchTarget}</b>}
            {!data.message && ' Please contact for availability before visiting.'}
          </div>
          <StoreList
            initialStores={data.stores}
            allStores={data.all_stores}
            userLocation={userLocation}
          />
        </div>
      );
    }

    // --- NO MATCH ---
    if (responseType === 'no_match') {
      const rawMsg = data?.message || msg.text || '';
      const formattedMsg = rawMsg.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br/>');
      return (
        <div className="no-match-container">
          <span className="no-match-icon">😔</span>
          <span dangerouslySetInnerHTML={{ __html: formattedMsg }} />
        </div>
      );
    }

    // --- CHAT RESPONSE: HTML from LLM (backward compat) ---
    const text = data?.message || msg.text || '';
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br/>');

    // Support [MAP: lat,lon] tokens in LLM responses
    const mapRegex = /(\[MAP:\s*[\d.-]+,\s*[\d.-]+\])/g;
    const parts = formattedText.split(mapRegex);

    if (parts.length > 1) {
      return (
        <>
          {parts.map((part, index) => {
            const mapMatch = part.match(/\[MAP:\s*([\d.-]+),\s*([\d.-]+)\]/);
            if (mapMatch) {
              return <MapComponent key={index} lat={mapMatch[1]} lon={mapMatch[2]} userLocation={userLocation} />;
            } else if (part.trim()) {
              return <div key={index} dangerouslySetInnerHTML={{ __html: part }} />;
            }
            return null;
          })}
        </>
      );
    }

    return <div dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  return (
    <div className={`chat-container ${isWidget ? 'widget-mode' : ''}`}>
      <header className="chat-header">
        <img src={getsuvLogo} alt="getSUV.AI" className="app-logo" />
        <h1>getSUV.AI</h1>
      </header>
      <div className="message-list">
        {messages.map((msg) => (
          <div key={msg.id} className={`message-bubble ${msg.sender}`}>
            {renderMessageContent(msg)}
          </div>
        ))}
        {loading && <LoadingIndicator />}
        <div ref={messagesEndRef} />
      </div>
      <div className="location-bar">
        <span className="location-status" title={locationStatusText}>
          📍 {locationStatusText}
        </span>
        <div className="location-actions">
          <button
            type="button"
            className="location-action-btn"
            onClick={handleChangeLocation}
            disabled={loading}
          >
            Change location
          </button>
          {locationSource === 'pincode' && (
            <button
              type="button"
              className="location-action-btn location-action-btn-secondary"
              onClick={handleUseCurrentLocation}
              disabled={loading}
            >
              Use current location
            </button>
          )}
        </div>
      </div>
      <div className="input-area">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          placeholder={isWaitingForLocation ? "Enter postal code or address..." : "Ask a question..."}
        />
        <button onClick={handleSend}>Send</button>
      </div>
    </div>
  );
}

export default App;
